import type { HTMLAttributes, KeyboardEvent } from 'react';

// Card primitive. Variants per the bundle's preview/cards-pillar.html
// and preview/cards-investment.html.

export type CardVariant = 'default' | 'pillar' | 'investment' | 'add-investment' | 'callout';

// `padding="none"` removes the default `p-4 sm:p-5` so callers like the
// Subscribe panels can host their own header/body/footer rows with their
// own internal padding. Don't fight Card's defaults via className overrides
// — Tailwind v4 source-order makes that brittle.
export type CardPadding = 'default' | 'none';

export type CardProps = HTMLAttributes<HTMLDivElement> & {
  variant?: CardVariant;
  padding?: CardPadding;
  interactive?: boolean;
};

const variants: Record<CardVariant, string> = {
  default: 'bg-card border border-border rounded-card shadow-card transition-all duration-200',
  pillar:
    'bg-card-warm border border-border rounded-card shadow-card transition-all duration-200',
  investment:
    'bg-card border border-border rounded-card shadow-card transition-all duration-200',
  'add-investment':
    'bg-transparent border-2 border-dashed border-border-strong rounded-card text-foreground-mute',
  // callout: primary-surface urgency panel (terracotta). Use in place of
  // ActionRequiredCallout patterns — avoids bg-card/border-border base
  // class conflicts in Tailwind v4 source-order resolution.
  callout:
    'bg-primary-soft border border-primary-line rounded-card shadow-card transition-all duration-200',
};

const paddings: Record<CardPadding, string> = {
  default: 'p-4 sm:p-5',
  none: '',
};

const interactiveClass = 'hover:shadow-hover hover:scale-[1.01] cursor-pointer';

export function Card({
  variant = 'default',
  padding = 'default',
  interactive = false,
  className = '',
  onClick,
  onKeyDown,
  children,
  ...rest
}: CardProps) {
  const isClickable = interactive && !!onClick;

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
    }
    onKeyDown?.(e);
  }

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={handleKeyDown}
      onClick={onClick}
      className={`${variants[variant]} ${paddings[padding]} ${interactive ? interactiveClass : ''} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}
